/**
 * <animated-can> — rotates a sequence of pre-rendered frame images based on
 * the element's scroll progress through the viewport.
 *
 * All frames are rendered up-front as stacked <img> elements with opacity 0;
 * the JS only toggles a `is-current` class so the swap is a compositor-only
 * change after first paint (no network, no decode, no flash). The browser
 * eagerly decodes every frame on initial render, so the very first scroll-
 * through is as smooth as the second.
 *
 * Expected DOM:
 *   <animated-can data-reduce-motion-frame="0">
 *     <div data-animated-can-stack>
 *       <img class="animated-can__frame is-current" data-frame-index="0" ...>
 *       <img class="animated-can__frame"             data-frame-index="1" ...>
 *       ...
 *     </div>
 *   </animated-can>
 *
 * Behaviour:
 *   progress 0 = section's top edge sits at the viewport bottom
 *   progress 1 = section's bottom edge sits at the viewport top
 * Progress is exposed as `--animated-can-progress` so CSS can drive copy
 * panel cross-fades without extra JS.
 */
class AnimatedCan extends HTMLElement {
  connectedCallback() {
    this.stack = this.querySelector('[data-animated-can-stack]');
    if (!this.stack) return;

    this.frames = Array.from(this.stack.querySelectorAll('.animated-can__frame'));
    if (!this.frames.length) return;

    this.frameCount = this.frames.length;
    this.currentFrame = 0;
    this.ready = false;
    this.frameAngles = this.buildFrameAngles();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const fallback = parseInt(this.dataset.reduceMotionFrame || '0', 10);
      this.swapTo(Math.min(this.frameCount - 1, Math.max(0, fallback)));
      return;
    }

    this.boundSchedule = this.scheduleUpdate.bind(this);

    // Force every frame to fully decode before we activate the scroll-driven
    // swap. Without this, the very first scroll can swap to a frame the
    // browser hasn't decoded yet, even though loading="eager" kicked off the
    // network request — the visible result is a one-tick flash of the
    // section bg through the can. We pin frame 0 until decode resolves for
    // every frame. Decode failures (e.g. a frame that 404s) shouldn't lock
    // the section, so we fall through after a 3s cap.
    const decodeAll = Promise.all(this.frames.map((f) => f.decode().catch(() => null)));
    const cap = new Promise((resolve) => setTimeout(resolve, 3000));
    Promise.race([decodeAll, cap]).then(() => this.activate());

    // Pause work when the section is well off-screen.
    this.io = new IntersectionObserver((entries) => {
      this.inView = entries[0].isIntersecting;
      if (this.inView && this.ready) this.scheduleUpdate();
    }, { rootMargin: '50% 0px' });
    this.io.observe(this);
  }

  activate() {
    if (this.ready) return;
    this.ready = true;
    window.addEventListener('scroll', this.boundSchedule, { passive: true });
    window.addEventListener('resize', this.boundSchedule, { passive: true });
    this.scheduleUpdate();
  }

  disconnectedCallback() {
    if (this.boundSchedule && this.ready) {
      window.removeEventListener('scroll', this.boundSchedule);
      window.removeEventListener('resize', this.boundSchedule);
    }
    if (this.io) this.io.disconnect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  scheduleUpdate() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  }

  update() {
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const total = rect.height + vh;
    const traveled = vh - rect.top;
    const progress = Math.max(0, Math.min(1, traveled / total));

    this.style.setProperty('--animated-can-progress', progress.toFixed(4));

    /* Rotate the can front→back over progress 0.16..0.34. R5 QA (2026-06-26
       client: the pin "comes to a dead stop … feels like you've come to the
       bottom of the page", then "quicken it up a bit more"). The runway was
       shortened 200vh→130vh and the choreography tightened so the whole thing
       plays quicker: faster spin (0.18-wide window), MVP copy reveals (fade
       starts 0.32) as the can settles, and the four lines cascade faster
       (0.36..0.53, ×20) — all done before the sticky inner un-pins (~0.56 on
       the 130vh runway), leaving a short ~0.03 settle. Holds frame 0 (front)
       until 0.16, frame N-1 (back) after 0.34. 60 frames over the 0.18 window
       step ~0.003 apart — still no stutter. See the section CSS for the rest. */
    const eased = this.constructor.easeInOut(progress, 0.16, 0.34);
    this.swapTo(this.frameForRotation(eased));
  }

  /* The shipped frame sequence is angularly non-uniform: Jason's 260610
     re-render covers the front→side portion at ~7× density, then the
     original 260515 frames finish the turn. `data-frame-segments="49:0.3824"`
     declares that the first 49 frames uniformly cover rotation fraction
     0→0.3824 and the remaining frames uniformly cover the rest. Without the
     attribute every frame is assumed evenly spaced (the original behavior). */
  buildFrameAngles() {
    const angles = new Array(this.frameCount);
    const spec = (this.dataset.frameSegments || '').match(/^(\d+):(0?\.\d+)$/);
    const c1 = spec ? Math.min(parseInt(spec[1], 10), this.frameCount) : this.frameCount;
    const b = spec ? parseFloat(spec[2]) : 1;
    for (let i = 0; i < c1; i++) angles[i] = c1 > 1 ? (i / (c1 - 1)) * b : 0;
    for (let i = c1; i < this.frameCount; i++) {
      angles[i] = b + ((i - c1 + 1) / (this.frameCount - c1)) * (1 - b);
    }
    return angles;
  }

  /* Nearest frame to the target rotation fraction. Walks from the current
     frame, so per-tick cost tracks scroll delta (frames are ordered). */
  frameForRotation(target) {
    let idx = this.currentFrame;
    while (idx < this.frameCount - 1 && this.frameAngles[idx] < target) idx++;
    while (idx > 0 && this.frameAngles[idx] > target) idx--;
    // idx is now the last frame at-or-below target; check the next one too.
    if (
      idx < this.frameCount - 1 &&
      Math.abs(this.frameAngles[idx + 1] - target) < Math.abs(this.frameAngles[idx] - target)
    ) {
      idx++;
    }
    return idx;
  }

  swapTo(idx) {
    if (idx === this.currentFrame) return;
    this.frames[this.currentFrame].classList.remove('is-current');
    this.frames[idx].classList.add('is-current');
    this.currentFrame = idx;
  }

  static easeInOut(p, low, high) {
    if (p <= low) return 0;
    if (p >= high) return 1;
    return (p - low) / (high - low);
  }
}

if (!customElements.get('animated-can')) {
  customElements.define('animated-can', AnimatedCan);
}
